import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import styled from 'styled-components';
// Remove direct Supabase import
// import { supabase } from '../../config/supabase';
import logo from '../../assets/closeup-logo.png';
import { Link } from 'react-router-dom';

const AdminDashboard = ({ onLogout }) => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin?operation=list`
        : `http://localhost:3001/api/admin?operation=list`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load stories');
      }

      const result = await response.json();
      setStories(result.data || []);
    } catch (error) {
      console.error('Error loading stories:', error);
      setError('Failed to load stories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    try {
      setStatusUpdateLoading(true);
      const newStatus = currentStatus === 1 ? 0 : 1;
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin?operation=update`
        : `http://localhost:3001/api/admin?operation=update`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id,
          status: newStatus
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update story status');
      }
      
      // Update local state
      setStories(stories.map(story => 
        story.id === id ? { ...story, status: newStatus } : story
      ));
    } catch (error) {
      console.error('Error updating story status:', error);
      setError('Failed to update story status. Please try again.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const confirmDelete = (story) => {
    setStoryToDelete(story);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      if (!storyToDelete) return;
      
      // Determine API URL based on environment
      const apiUrl = process.env.NODE_ENV === 'production'
        ? `/api/admin?operation=delete`
        : `http://localhost:3001/api/admin?operation=delete`;
      
      // Call the backend API instead of Supabase directly
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: storyToDelete.id
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete story');
      }
      
      // Close modal and update local state
      setShowDeleteModal(false);
      setStories(stories.filter(story => story.id !== storyToDelete.id));
      setStoryToDelete(null);
    } catch (error) {
      console.error('Error deleting story:', error);
      setError('Failed to delete story. Please try again.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminLoggedIn');
    onLogout();
  };

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner animation="border" variant="danger" />
        <p>Loading stories...</p>
      </LoadingContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Link to="/">
          <Logo src={logo} alt="Closeup Logo" />
        </Link>
        <HeaderTitle>Admin Dashboard</HeaderTitle>
        <LogoutButton variant="outline-light" onClick={handleLogout}>
          Logout
        </LogoutButton>
      </Header>

      <Content>
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
            <Button 
              variant="outline-danger" 
              size="sm" 
              className="ms-3"
              onClick={loadStories}
            >
              Try Again
            </Button>
          </Alert>
        )}

        <StatsRow>
          <StatCard>
            <StatValue>{stories.length}</StatValue>
            <StatLabel>Total Stories</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stories.filter(s => s.status === 1).length}</StatValue>
            <StatLabel>Approved</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stories.filter(s => s.status === 0).length}</StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
        </StatsRow>

        <TableContainer>
          <h4 className="mb-4">All Stories</h4>
          
          {stories.length === 0 ? (
            <Alert variant="info">No stories found.</Alert>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Story Type</th>
                  <th>Story</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stories.map(story => (
                  <tr key={story.id}>
                    <td>{story.id}</td>
                    <td>{new Date(story.created_at).toLocaleDateString()}</td>
                    <td>{story.is_anonymous ? 'Anonymous' : story.name}</td>
                    <td>{story.email}</td>
                    <td>
                      <Badge bg={story.story_type === 'love' ? 'danger' : 'secondary'}>
                        {story.story_type === 'love' ? 'Love Story' : 'Boot Story'}
                      </Badge>
                    </td>
                    <td>
                      <StoryText>{story.story}</StoryText>
                    </td>
                    <td>
                      <Badge bg={story.status === 1 ? 'success' : 'warning'}>
                        {story.status === 1 ? 'Approved' : 'Pending'}
                      </Badge>
                    </td>
                    <td>
                      <ActionButtons>
                        <Button
                          variant={story.status === 1 ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          onClick={() => handleStatusChange(story.id, story.status)}
                          disabled={statusUpdateLoading}
                        >
                          {story.status === 1 ? 'Unpublish' : 'Approve'}
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => confirmDelete(story)}
                        >
                          Delete
                        </Button>
                      </ActionButtons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </TableContainer>
      </Content>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this story? This action cannot be undone.
          
          {storyToDelete && (
            <div className="mt-3 p-3 bg-light">
              <strong>Story:</strong> {storyToDelete.story.substring(0, 100)}...
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </DashboardContainer>
  );
};

const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const Header = styled.header`
  background-color: #e31837;
  color: white;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Logo = styled.img`
  height: 40px;
  margin-right: 1rem;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  flex-grow: 1;
`;

const LogoutButton = styled(Button)`
  margin-left: auto;
`;

const Content = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-top: 2rem;
`;

const StoryText = styled.div`
  max-height: 100px;
  overflow-y: auto;
  white-space: pre-wrap;
  font-size: 0.9rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 1rem;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #e31837;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  color: #6c757d;
  font-size: 1rem;
`;

export default AdminDashboard;
